import React, { useState, useEffect, useCallback } from 'react';
import './PlanWizard.css';

// Goal presets with recommended plan templates
const GOAL_PRESETS = [
  {
    id: 'muscle',
    emoji: '💪',
    title: 'Build Muscle',
    subtitle: 'Hypertrophy & Strength',
    description: 'Focus on progressive overload and muscle growth',
    color: '#ef4444',
    recommendedPlan: {
      title: 'Muscle Building Program',
      goal: 'Build lean muscle mass through progressive resistance training',
      items: [
        { type: 'workout', frequency: 2, unit: 'days', name: 'Strength Training' },
        { type: 'meal', frequency: 1, unit: 'days', name: 'Nutrition Log' },
        { type: 'metric', frequency: 7, unit: 'days', name: 'Weekly Weigh-in' },
        { type: 'media', frequency: 7, unit: 'days', name: 'Progress Photo' },
      ]
    }
  },
  {
    id: 'lose',
    emoji: '🔥',
    title: 'Lose Weight',
    subtitle: 'Fat Loss & Cardio',
    description: 'Combine training with nutrition tracking for sustainable fat loss',
    color: '#f59e0b',
    recommendedPlan: {
      title: 'Fat Loss Program',
      goal: 'Lose body fat while preserving muscle through balanced training and nutrition',
      items: [
        { type: 'workout', frequency: 2, unit: 'days', name: 'Fat Burning Workout' },
        { type: 'meal', frequency: 1, unit: 'days', name: 'Calorie Tracking' },
        { type: 'metric', frequency: 3, unit: 'days', name: 'Body Measurements' },
        { type: 'media', frequency: 7, unit: 'days', name: 'Progress Photo' },
      ]
    }
  },
  {
    id: 'health',
    emoji: '❤️',
    title: 'Get Healthy',
    subtitle: 'Wellness & Balance',
    description: 'Build sustainable habits for long-term health',
    color: '#10b981',
    recommendedPlan: {
      title: 'Wellness Program',
      goal: 'Improve overall health through consistent exercise and mindful nutrition',
      items: [
        { type: 'workout', frequency: 2, unit: 'days', name: 'Wellness Workout' },
        { type: 'meal', frequency: 1, unit: 'days', name: 'Healthy Eating Log' },
        { type: 'metric', frequency: 7, unit: 'days', name: 'Health Check-in' },
        { type: 'survey', frequency: 7, unit: 'days', name: 'Weekly Reflection' },
      ]
    }
  },
  {
    id: 'strength',
    emoji: '🏋️',
    title: 'Get Stronger',
    subtitle: 'Powerlifting & PRs',
    description: 'Maximize strength gains and hit new personal records',
    color: '#8b5cf6',
    recommendedPlan: {
      title: 'Strength Program',
      goal: 'Increase maximal strength in compound lifts through periodized training',
      items: [
        { type: 'workout', frequency: 2, unit: 'days', name: 'Strength Session' },
        { type: 'meal', frequency: 1, unit: 'days', name: 'Performance Nutrition' },
        { type: 'metric', frequency: 7, unit: 'days', name: 'Strength Tracking' },
      ]
    }
  },
  {
    id: 'endurance',
    emoji: '🏃',
    title: 'Build Endurance',
    subtitle: 'Cardio & Stamina',
    description: 'Improve cardiovascular fitness and endurance',
    color: '#3b82f6',
    recommendedPlan: {
      title: 'Endurance Program',
      goal: 'Build cardiovascular endurance and stamina through progressive cardio training',
      items: [
        { type: 'workout', frequency: 2, unit: 'days', name: 'Cardio Session' },
        { type: 'meal', frequency: 1, unit: 'days', name: 'Energy Nutrition' },
        { type: 'metric', frequency: 7, unit: 'days', name: 'Performance Metrics' },
      ]
    }
  },
  {
    id: 'custom',
    emoji: '✨',
    title: 'Custom Goal',
    subtitle: 'Define Your Own',
    description: 'Create a personalized plan tailored to your unique goals',
    color: '#6366f1',
    recommendedPlan: null
  }
];

const ACTIVITY_TYPE_INFO = {
  workout: { emoji: '🏋️', label: 'Workouts', description: 'Strength, cardio, or custom training sessions' },
  meal: { emoji: '🥗', label: 'Meals', description: 'Track nutrition and eating habits' },
  metric: { emoji: '📊', label: 'Metrics', description: 'Weight, measurements, and body stats' },
  media: { emoji: '📸', label: 'Photos', description: 'Visual progress tracking' },
  survey: { emoji: '📝', label: 'Check-ins', description: 'Mood, energy, and wellness surveys' },
};

const WIZARD_STEPS = ['goals', 'customize', 'activities', 'review'];

const PlanWizard = ({ 
  close, 
  modalService, 
  backendService, 
  onPlanCreated,
  isOnboarding = false 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [customGoal, setCustomGoal] = useState('');
  const [planTitle, setPlanTitle] = useState('');
  const [planGoalStatement, setPlanGoalStatement] = useState('');
  const [selectedActivities, setSelectedActivities] = useState({
    workout: { enabled: true, frequency: 2, unit: 'days' },
    meal: { enabled: true, frequency: 1, unit: 'days' },
    metric: { enabled: true, frequency: 7, unit: 'days' },
    media: { enabled: false, frequency: 7, unit: 'days' },
    survey: { enabled: false, frequency: 7, unit: 'days' },
  });
  const [selectedMeals, setSelectedMeals] = useState({
    Breakfast: true,
    Lunch: true,
    Dinner: true,
    Snacks: false,
  });
  const [selectedWorkoutDays, setSelectedWorkoutDays] = useState({
    Monday: true,
    Tuesday: false,
    Wednesday: true,
    Thursday: false,
    Friday: true,
    Saturday: false,
    Sunday: false,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [, setCreatedPlanId] = useState(null);

  const stepName = WIZARD_STEPS[currentStep];

  // Update plan details when goal is selected
  useEffect(() => {
    if (selectedGoal && selectedGoal.recommendedPlan) {
      setPlanTitle(selectedGoal.recommendedPlan.title);
      setPlanGoalStatement(selectedGoal.recommendedPlan.goal);
      
      // Set recommended activities based on the goal preset
      setSelectedActivities(prev => {
        const newActivities = {};
        Object.keys(prev).forEach(type => {
          newActivities[type] = { ...prev[type], enabled: false };
        });
        
        selectedGoal.recommendedPlan.items.forEach(item => {
          if (newActivities[item.type]) {
            newActivities[item.type] = {
              enabled: true,
              frequency: item.frequency,
              unit: item.unit,
              name: item.name,
            };
          }
        });
        
        return newActivities;
      });
    }
  }, [selectedGoal]);

  const handleGoalSelect = (goal) => {
    setSelectedGoal(goal);
    if (goal.id !== 'custom') {
      // Auto-advance for preset goals
      setTimeout(() => setCurrentStep(1), 300);
    }
  };

  const handleCustomGoalSubmit = () => {
    if (customGoal.trim()) {
      setPlanTitle('My Fitness Plan');
      setPlanGoalStatement(customGoal);
      setCurrentStep(1);
    }
  };

  const toggleActivity = (type) => {
    setSelectedActivities(prev => ({
      ...prev,
      [type]: { ...prev[type], enabled: !prev[type].enabled }
    }));
  };

  const updateActivityFrequency = (type, frequency) => {
    setSelectedActivities(prev => ({
      ...prev,
      [type]: { ...prev[type], frequency: parseInt(frequency) || 1 }
    }));
  };

  const updateActivityUnit = (type, unit) => {
    setSelectedActivities(prev => ({
      ...prev,
      [type]: { ...prev[type], unit }
    }));
  };

  const getInstructionForType = (type) => {
    switch (type) {
      case 'workout': return 'do_workout_regenerated_every_time';
      case 'meal': return 'eat_meal';
      case 'metric': return 'take_measurements';
      case 'media': return 'upload_media';
      case 'survey': return 'take_survey';
      default: return 'do_workout_generated_manually';
    }
  };

  const createPlanWithItems = useCallback(async () => {
    setIsCreating(true);
    
    try {
      // 1. Create the plan
      const plan = await backendService.createPlan(planTitle, planGoalStatement);
      const planId = plan._id;
      setCreatedPlanId(planId);
      
      // 2. Set as active plan
      await backendService.setPlanAffiliation(planId);
      
      // 3. Create plan items for each enabled activity
      const enabledActivities = Object.entries(selectedActivities)
        .filter(([_, config]) => config.enabled);
      
      for (const [type, config] of enabledActivities) {
        // Special handling for meals - create selected meals
        if (type === 'meal') {
          const mealNames = Object.entries(selectedMeals)
            .filter(([_, enabled]) => enabled)
            .map(([name]) => name);
          for (const mealName of mealNames) {
            const item = {
              start_time: new Date().toISOString().split('T')[0],
              recurrence_number: config.frequency.toString(),
              recurrence_unit: config.unit,
              instruction: getInstructionForType(type),
              user_can_modify: true,
              autogeneration_hint: mealName,
              completion_days: 1,
            };
            await backendService.savePlanItem(planId, null, item);
          }
        } 
        // Special handling for workouts - create one per selected day
        else if (type === 'workout') {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const selectedDays = Object.entries(selectedWorkoutDays)
            .filter(([_, enabled]) => enabled)
            .map(([day]) => day);
          
          for (const day of selectedDays) {
            // Calculate the next occurrence of this day
            const today = new Date();
            const todayDayIndex = today.getDay();
            const targetDayIndex = dayNames.indexOf(day);
            let daysUntil = targetDayIndex - todayDayIndex;
            if (daysUntil < 0) daysUntil += 7;
            if (daysUntil === 0) daysUntil = 0; // Today is fine
            
            const startDate = new Date(today);
            startDate.setDate(today.getDate() + daysUntil);
            
            const item = {
              start_time: startDate.toISOString().split('T')[0],
              recurrence_number: '1', // Weekly
              recurrence_unit: 'weeks',
              instruction: getInstructionForType(type),
              user_can_modify: true,
              autogeneration_hint: `${day} Workout`,
              completion_days: 3,
            };
            await backendService.savePlanItem(planId, null, item);
          }
        } else {
          const item = {
            start_time: new Date().toISOString().split('T')[0],
            recurrence_number: config.frequency.toString(),
            recurrence_unit: config.unit,
            instruction: getInstructionForType(type),
            user_can_modify: true,
            autogeneration_hint: config.name || ACTIVITY_TYPE_INFO[type].label,
            completion_days: type === 'media' ? 7 : 1,
          };
          
          await backendService.savePlanItem(planId, null, item);
        }
      }
      
      // 4. Generate workouts if there are workout items
      const hasWorkouts = selectedActivities.workout.enabled;
      if (hasWorkouts) {
        setIsGenerating(true);
        try {
          await backendService.refreshWorkouts(planId);
        } catch (genError) {
          console.error('Workout generation failed:', genError);
          // Continue anyway - user can regenerate later
        }
        setIsGenerating(false);
      }
      
      // 5. Generate planned activities immediately
      try {
        await backendService.generatePlannedActivities();
      } catch (genError) {
        console.error('Activity generation failed:', genError);
        // Continue anyway - activities will be generated by cron
      }
      
      setShowSuccess(true);
      
      // Notify parent with planId so it can refresh activities
      if (onPlanCreated) {
        onPlanCreated(planId);
      }
      
    } catch (error) {
      console.error('Failed to create plan:', error);
      alert('Failed to create plan. Please try again.');
    } finally {
      setIsCreating(false);
      setIsGenerating(false);
    }
  }, [backendService, planTitle, planGoalStatement, selectedActivities, selectedMeals, selectedWorkoutDays, onPlanCreated]);

  const nextStep = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      createPlanWithItems();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const canProceed = () => {
    switch (stepName) {
      case 'goals':
        return selectedGoal !== null && (selectedGoal.id !== 'custom' || customGoal.trim().length > 0);
      case 'customize':
        return planTitle.trim().length > 0;
      case 'activities':
        const hasEnabledActivity = Object.values(selectedActivities).some(a => a.enabled);
        // If meals are enabled, at least one meal must be selected
        const mealsValid = !selectedActivities.meal.enabled || Object.values(selectedMeals).some(v => v);
        // If workouts are enabled, at least one day must be selected
        const workoutsValid = !selectedActivities.workout.enabled || Object.values(selectedWorkoutDays).some(v => v);
        return hasEnabledActivity && mealsValid && workoutsValid;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const getEnabledActivitiesCount = () => {
    return Object.values(selectedActivities).filter(a => a.enabled).length;
  };

  // Render goal selection step
  const renderGoalsStep = () => (
    <div className="wizard-step goals-step">
      <div className="step-header">
        <h2 className="step-title">What's Your Goal? 🎯</h2>
        <p className="step-subtitle">Choose a goal and we'll create a personalized plan for you</p>
      </div>
      
      <div className="goals-grid">
        {GOAL_PRESETS.map(goal => (
          <button
            key={goal.id}
            className={`goal-card ${selectedGoal?.id === goal.id ? 'selected' : ''}`}
            onClick={() => handleGoalSelect(goal)}
            style={{ '--goal-color': goal.color }}
          >
            <div className="goal-emoji">{goal.emoji}</div>
            <div className="goal-content">
              <h3 className="goal-title">{goal.title}</h3>
              <span className="goal-subtitle">{goal.subtitle}</span>
              <p className="goal-description">{goal.description}</p>
            </div>
            {selectedGoal?.id === goal.id && (
              <div className="goal-check">✓</div>
            )}
          </button>
        ))}
      </div>

      {selectedGoal?.id === 'custom' && (
        <div className="custom-goal-input">
          <textarea
            value={customGoal}
            onChange={(e) => setCustomGoal(e.target.value)}
            placeholder="Describe your fitness goal in detail... (e.g., 'Train for a marathon while maintaining upper body strength')"
            className="custom-goal-textarea"
            rows={3}
          />
          <button 
            className="btn-custom-goal-submit"
            onClick={handleCustomGoalSubmit}
            disabled={!customGoal.trim()}
          >
            Continue with Custom Goal →
          </button>
        </div>
      )}
    </div>
  );

  // Render customize step
  const renderCustomizeStep = () => (
    <div className="wizard-step customize-step">
      <div className="step-header">
        <h2 className="step-title">Customize Your Plan ✨</h2>
        <p className="step-subtitle">Fine-tune the details to match your preferences</p>
      </div>

      <div className="customize-form">
        <div className="form-field">
          <label className="field-label">Plan Name</label>
          <input
            type="text"
            value={planTitle}
            onChange={(e) => setPlanTitle(e.target.value)}
            placeholder="e.g., My Strength Journey"
            className="field-input"
          />
        </div>

        <div className="form-field">
          <label className="field-label">Goal Statement</label>
          <textarea
            value={planGoalStatement}
            onChange={(e) => setPlanGoalStatement(e.target.value)}
            placeholder="What do you want to achieve? This helps the AI generate better workouts."
            className="field-textarea"
            rows={3}
          />
          <p className="field-hint">💡 Be specific! The AI uses this to personalize your workouts.</p>
        </div>

        {selectedGoal && (
          <div className="goal-preview">
            <div className="goal-preview-badge" style={{ background: selectedGoal.color }}>
              {selectedGoal.emoji} {selectedGoal.title}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render activities step
  const renderActivitiesStep = () => (
    <div className="wizard-step activities-step">
      <div className="step-header">
        <h2 className="step-title">What Will You Track? 📋</h2>
        <p className="step-subtitle">Choose the activities to include in your plan</p>
      </div>

      <div className="activities-list">
        {Object.entries(ACTIVITY_TYPE_INFO).map(([type, info]) => {
          const config = selectedActivities[type];
          return (
            <div 
              key={type} 
              className={`activity-config-card ${config.enabled ? 'enabled' : ''}`}
            >
              <div className="activity-toggle-row">
                <button 
                  className="activity-toggle"
                  onClick={() => toggleActivity(type)}
                >
                  <span className="activity-emoji">{info.emoji}</span>
                  <div className="activity-info">
                    <span className="activity-label">{info.label}</span>
                    <span className="activity-description">{info.description}</span>
                  </div>
                  <div className={`wizard-toggle-switch ${config.enabled ? 'on' : ''}`}>
                    <div className="wizard-toggle-knob" />
                  </div>
                </button>
              </div>

              {config.enabled && type !== 'meal' && type !== 'workout' && (
                <div className="activity-frequency">
                  <span className="frequency-label">Repeat every</span>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={config.frequency}
                    onChange={(e) => updateActivityFrequency(type, e.target.value)}
                    className="frequency-input"
                  />
                  <select
                    value={config.unit}
                    onChange={(e) => updateActivityUnit(type, e.target.value)}
                    className="frequency-select"
                  >
                    <option value="days">{config.frequency === 1 ? 'day' : 'days'}</option>
                    <option value="weeks">{config.frequency === 1 ? 'week' : 'weeks'}</option>
                    <option value="months">{config.frequency === 1 ? 'month' : 'months'}</option>
                  </select>
                </div>
              )}

              {config.enabled && type === 'workout' && (
                <div className="activity-frequency workout-days-selection">
                  <span className="frequency-label">Training days:</span>
                  <div className="workout-day-checkboxes">
                    {Object.entries(selectedWorkoutDays).map(([day, enabled]) => (
                      <label key={day} className={`workout-day-label ${enabled ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() => setSelectedWorkoutDays(prev => ({
                            ...prev,
                            [day]: !prev[day]
                          }))}
                          className="workout-day-checkbox"
                        />
                        <span className="workout-day-text">{day.slice(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {config.enabled && type === 'meal' && (
                <div className="activity-frequency meal-selection">
                  <span className="frequency-label">Track daily:</span>
                  <div className="meal-checkboxes">
                    {Object.entries(selectedMeals).map(([mealName, enabled]) => (
                      <label key={mealName} className="meal-checkbox-label">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() => setSelectedMeals(prev => ({
                            ...prev,
                            [mealName]: !prev[mealName]
                          }))}
                          className="meal-checkbox"
                        />
                        <span className="meal-checkbox-text">{mealName}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="activities-summary">
        <span className="summary-count">{getEnabledActivitiesCount()}</span>
        <span className="summary-text">activity types selected</span>
      </div>
    </div>
  );

  // Render review step
  const renderReviewStep = () => (
    <div className="wizard-step review-step">
      <div className="step-header">
        <h2 className="step-title">Review Your Plan 🚀</h2>
        <p className="step-subtitle">Everything look good? Let's create it!</p>
      </div>

      <div className="review-card">
        <div className="review-header">
          <span className="review-goal-emoji">{selectedGoal?.emoji || '✨'}</span>
          <div className="review-titles">
            <h3 className="review-plan-title">{planTitle}</h3>
            <span className="review-goal-type">{selectedGoal?.title || 'Custom Goal'}</span>
          </div>
        </div>

        <div className="review-goal-statement">
          <span className="review-label">Goal:</span>
          <p>{planGoalStatement}</p>
        </div>

        <div className="review-activities">
          <span className="review-label">Weekly Schedule:</span>
          <div className="review-activity-list">
            {Object.entries(selectedActivities)
              .filter(([_, config]) => config.enabled)
              .map(([type, config]) => {
                let freqText;
                if (type === 'meal') {
                  freqText = `Daily: ${Object.entries(selectedMeals).filter(([_, e]) => e).map(([n]) => n).join(', ')}`;
                } else if (type === 'workout') {
                  const days = Object.entries(selectedWorkoutDays).filter(([_, e]) => e).map(([d]) => d.slice(0, 3));
                  freqText = days.join(', ');
                } else {
                  freqText = `Every ${config.frequency} ${config.frequency === 1 ? config.unit.replace('s', '') : config.unit}`;
                }
                return (
                  <div key={type} className="review-activity-item">
                    <span className="review-activity-emoji">{ACTIVITY_TYPE_INFO[type].emoji}</span>
                    <span className="review-activity-name">{ACTIVITY_TYPE_INFO[type].label}</span>
                    <span className="review-activity-freq">{freqText}</span>
                  </div>
                );
              })}
          </div>
        </div>

        {selectedActivities.workout.enabled && (
          <div className="review-ai-note">
            <span className="ai-sparkle">✨</span>
            <p>AI will generate personalized workouts based on your goal</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render success state
  const renderSuccess = () => (
    <div className="wizard-success">
      <div className="success-animation">
        <div className="success-circle">
          <span className="success-check">✓</span>
        </div>
        <div className="success-sparkles">
          {['✨', '🎉', '⭐', '💪', '🔥'].map((emoji, i) => (
            <span key={i} className="success-sparkle" style={{ '--delay': `${i * 0.1}s` }}>
              {emoji}
            </span>
          ))}
        </div>
      </div>
      
      <h2 className="success-title">Plan Created! 🎉</h2>
      <p className="success-message">
        Your personalized plan is ready. Start tracking your journey!
      </p>

      <div className="success-actions">
        <button 
          className="btn-success-primary"
          onClick={() => close && close()}
        >
          Start My Journey →
        </button>
      </div>
    </div>
  );

  // Render creating state
  const renderCreating = () => (
    <div className="wizard-creating">
      <div className="creating-animation">
        <div className="creating-spinner" />
        <div className="creating-icons">
          {isGenerating ? (
            <>
              <span className="creating-icon">🤖</span>
              <span className="creating-text">AI is crafting your workouts...</span>
            </>
          ) : (
            <>
              <span className="creating-icon">⚡</span>
              <span className="creating-text">Setting up your plan...</span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Don't show navigation on success or creating
  if (showSuccess) {
    return (
      <div className="plan-wizard">
        {renderSuccess()}
      </div>
    );
  }

  if (isCreating) {
    return (
      <div className="plan-wizard">
        {renderCreating()}
      </div>
    );
  }

  return (
    <div className="plan-wizard">
      {/* Progress indicator */}
      <div className="wizard-progress">
        {WIZARD_STEPS.map((step, index) => (
          <div 
            key={step}
            className={`wizard-progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
          >
            <div className="wizard-progress-dot">
              {index < currentStep ? '✓' : index + 1}
            </div>
            <span className="wizard-progress-label">
              {step.charAt(0).toUpperCase() + step.slice(1)}
            </span>
          </div>
        ))}
        <div 
          className="wizard-progress-bar" 
          style={{ '--progress': `${(currentStep / (WIZARD_STEPS.length - 1)) * 100}%` }} 
        />
      </div>

      {/* Step content */}
      <div className="wizard-content">
        {stepName === 'goals' && renderGoalsStep()}
        {stepName === 'customize' && renderCustomizeStep()}
        {stepName === 'activities' && renderActivitiesStep()}
        {stepName === 'review' && renderReviewStep()}
      </div>

      {/* Navigation */}
      <div className="wizard-navigation">
        {currentStep > 0 && (
          <button className="btn-wizard-back" onClick={prevStep}>
            ← Back
          </button>
        )}
        
        {selectedGoal?.id !== 'custom' || currentStep > 0 ? (
          <button 
            className="btn-wizard-next"
            onClick={nextStep}
            disabled={!canProceed()}
          >
            {currentStep === WIZARD_STEPS.length - 1 ? (
              <>Create My Plan 🚀</>
            ) : (
              <>Continue →</>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default PlanWizard;
